import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { appListenerMiddleware } from 'reduxTyped'

import { AiConversationsRateSocketEventsSchemas } from 'apps/chat/interfaces/aiConversationsRateShema'
import { AiConversationsRateState } from 'apps/chat/models/chatInterfaces'
import { messagesSlice } from 'apps/chat/reducers/messagesSlice'
import { Ability } from 'common/core/constants/Ability'
import { getAbility } from 'common/core/selectors/abilitiesSelectors'
import { getUserRole, UserRoles } from 'common/userRoles'
import { AiConversationFeedbackApi } from 'shared/api/requests/aiConversationFeedback'
import {
  AiConversationRate,
  FeedbackCategory,
} from 'shared/api/requests/aiConversationFeedback/schemas'
import { createAsyncAction } from 'shared/lib/redux'
import { getClientId } from 'utils/clientId'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

import { setCurrentThread } from '../actions/threadsActions'
import { ChatMessageType } from '../types/chatMessageTypeList'
import { ChatMessage, ChatMessageInterface } from '../types/chatMessageTypes'

export const NAMESPACE = 'aiConversationsRate'

export const initialState: AiConversationsRateState = {
  conversations: {},
}

const { reducer, actions } = createSlice({
  initialState,
  name: NAMESPACE,
  reducers: {
    aiConversationsRateReset: (state) => {
      state.conversations = {}
    },
    aiConversationsRateUpdated: (
      state,
      action: PayloadAction<Record<string, AiConversationRate>>,
    ) => {
      state.conversations = {
        ...state.conversations,
        ...action.payload,
      }
    },
  },
})

const retrieveConversationsStatuses = createAsyncAction(
  `${NAMESPACE}/retrieveConversationsStatuses`,
  async (sessionIds: string[], { dispatch }) => {
    const { data } = await AiConversationFeedbackApi.retrieve({ body: { session_ids: sessionIds } })

    if (data.data !== null) {
      dispatch(actions.aiConversationsRateUpdated(data.data))
    }
  },
  {
    condition: (sessionIds: string[]) => sessionIds.length > 0,
  },
)

const saveConversationRate = createAsyncAction(
  `${NAMESPACE}/saveConversationRate`,
  async (
    {
      sessionId,
      isPositive,
      category,
      comment,
    }: {
      sessionId: string
      isPositive: boolean
      category: FeedbackCategory | null
      comment: string | null
    },
    { dispatch },
  ) => {
    await AiConversationFeedbackApi.save({
      body: {
        data: { session_id: sessionId, is_positive: isPositive, category, comment },
      },
      query: { client_id: getClientId() },
    })
    dispatch(
      actions.aiConversationsRateUpdated({
        [sessionId]: {
          session_id: sessionId,
          is_positive: isPositive,
        },
      }),
    )
  },
)

appListenerMiddleware.startListening({
  matcher: isAnyOf(
    messagesSlice.actions.getListFx.fulfilled,
    messagesSlice.actions.getNextListFx.fulfilled,
    messagesSlice.actions.getPrevListFx.fulfilled,
  ),
  effect: (action, { dispatch, getState }) => {
    const aiAlwaysOnAbility = getAbility(getState(), Ability.AI_ALWAYS_ON)
    const userRole = getUserRole(getState())

    if (!aiAlwaysOnAbility.isEnabled || ![UserRoles.ADMIN, UserRoles.EDITOR].includes(userRole)) {
      return
    }

    const conversationsIds = action.payload.messages
      .filter(
        (message: ChatMessageInterface) =>
          message.type === ChatMessageType.AI_AUTOMATION_INSTAGRAM_FINISHED,
      )
      .map((message: ChatMessage.AiAutomationFinished) => message.model.session_id)

    dispatch(retrieveConversationsStatuses(conversationsIds))
  },
})

appListenerMiddleware.startListening({
  actionCreator: setCurrentThread,
  effect: (action, { dispatch }) => {
    if (action.payload === null) {
      dispatch(actions.aiConversationsRateReset())
    }
  },
})

anotherTabNotificationsListener.on(
  AiConversationsRateSocketEventsSchemas.feedbackLeft,
  (payload, dispatch) => {
    dispatch(
      actions.aiConversationsRateUpdated({
        [payload.data.session_id]: payload.data,
      }),
    )
  },
)

export const AiConversationsRateActions = {
  setConversationStatus: saveConversationRate,
}

export const AiConversationsRateReducer = reducer
